import { ReactElement, useId, useState } from 'react';
import { Info, Trash2 } from 'lucide-react';

import config from '@/config';
import { Issue } from '@/constants';
import { useDrawerSwipeDirection } from '@shared/lib/hooks';
import { formatMegabytes } from '@shared/lib/format-bytes';
import {
	useDownloadedIssues,
	useIssueStorage,
	type IssueStorageBreakdown,
} from '@entities/downloaded-issue';
import { PreviewLink, previewTransitionName } from '@shared/ui/preview-link';
import { Loading } from '@shared/ui/loading';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@shared/ui/drawer';

import styles from './downloads-drawer.module.css';
import { useT } from '@shared/i18n';

/**
 * The home screen's entry point to downloaded issues -- same shape as
 * `widgets/favorites-drawer`, opened the same two ways (a `trigger`
 * element, or the caller's own `open`/`onOpenChange` state), and reusing
 * that widget's `useDrawerSwipeDirection` so both stay right-panel-on-
 * desktop/bottom-sheet-on-mobile without drifting apart. The list itself
 * is `entities/downloaded-issue`'s `useDownloadedIssues` -- the exact same
 * Dexie-backed hook `/downloads` (`pages/downloads`) already used, not a
 * second copy of that read/remove logic.
 *
 * Gated on `config.app.downloadedIssues.enabled`, same flag `/downloads`
 * and its `AppMenu` entry check.
 */
export function DownloadsDrawer({
	trigger,
	open: controlledOpen,
	onOpenChange,
}: DownloadsDrawerProps) {
	const t = useT();
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const open = controlledOpen ?? uncontrolledOpen;
	const swipeDirection = useDrawerSwipeDirection();
	const setOpen = (value: boolean) => {
		setUncontrolledOpen(value);
		onOpenChange?.(value);
	};

	if (!config.app.downloadedIssues?.enabled) {
		return null;
	}

	return (
		<Drawer
			open={open}
			onOpenChange={setOpen}
			swipeDirection={swipeDirection}
		>
			{trigger ? <DrawerTrigger render={trigger} /> : null}
			<DrawerContent className={styles['drawer']}>
				<DrawerHeader className={styles['header']}>
					<DrawerTitle className={styles['title']}>
						{t('common.downloads')}
					</DrawerTitle>
					<DrawerDescription className={styles['description']}>
						{t('downloads.description')}
					</DrawerDescription>
				</DrawerHeader>
				<DownloadsList />
			</DrawerContent>
		</Drawer>
	);
}

interface DownloadsDrawerProps {
	trigger?: ReactElement<Record<string, unknown>>;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

function DownloadsList() {
	const t = useT();
	const { isLoading, issues, removingIssueId, remove } =
		useDownloadedIssues();

	if (isLoading) {
		return (
			<div className={styles['loading']}>
				<Loading />
			</div>
		);
	}

	if (!issues.length) {
		return (
			<p className={styles['empty']}>
				{t('downloads.empty')}
			</p>
		);
	}

	// Same "only the list scrolls" shape as `favorites-drawer`'s own
	// `FavoritesList` -- the header above it is a `shrink-0` sibling.
	return (
		<ul className={styles['list']}>
			{issues.map((issue) => (
				<DownloadedIssueItem
					key={issue.id}
					issue={issue}
					isRemoving={removingIssueId === issue.id}
					onRemove={() => remove(issue.id)}
				/>
			))}
		</ul>
	);
}

function DownloadedIssueItem({
	issue,
	isRemoving,
	onRemove,
}: {
	issue: Issue;
	isRemoving: boolean;
	onRemove: () => void;
}) {
	const t = useT();
	const { id, cover, name, slug } = issue;
	const transitionName = previewTransitionName(slug ?? id);
	const { data: storage } = useIssueStorage(id);
	const [showDetails, setShowDetails] = useState(false);
	const detailsId = useId();

	return (
		<li className={styles['item']}>
			<div className={styles['row']}>
				{/* No close-the-drawer-first `onClick` here, deliberately --
				    same reasoning as `favorites-drawer`'s `FavoriteItem`: this
				    is a `push` navigation away from `/` (`PreviewLink`'s
				    `replace={false}`), so `Home` unmounts this drawer
				    regardless, and closing it here would race the
				    `?dialog=downloads` search param a caller syncing to it
				    (`basic`'s `Home`) needs intact on the current history
				    entry for Back to restore. */}
				<PreviewLink
					to="/downloads/issues/$issueId"
					params={{ issueId: slug ?? id }}
					payload={{ preview: { imageSrc: cover, transitionName } }}
					className={styles['link']}
				>
					<figure className={styles['cover']}>
						{cover ? (
							<img
								loading="lazy"
								src={cover}
								alt=""
								style={{ viewTransitionName: transitionName }}
							/>
						) : null}
					</figure>
					<div className={styles['text']}>
						<span dir="auto" className={styles['item-title']}>{name}</span>
						{/* Space is reserved even before the size resolves --
						    `.item-description`'s `min-height` (module CSS) --
						    so a still-loading card doesn't sit at a different
						    height than its neighbours. */}
						<span className={styles['item-description']}>
							{storage ? formatMegabytes(storage.totalBytes) : null}
						</span>
					</div>
				</PreviewLink>
				<button
					type="button"
					className={styles['info']}
					aria-label={t('downloads.details', { name })}
					aria-expanded={showDetails}
					aria-controls={detailsId}
					onClick={() => setShowDetails((value) => !value)}
				>
					<Info size={18} />
				</button>
				<button
					type="button"
					className={styles['remove']}
					aria-label={t('downloads.removeIssue', { name })}
					title={t('downloads.remove')}
					onClick={onRemove}
					disabled={isRemoving}
				>
					<Trash2 size={18} />
				</button>
			</div>
			{showDetails ? (
				<StorageDetails id={detailsId} storage={storage} />
			) : null}
		</li>
	);
}

function StorageDetails({
	id,
	storage,
}: {
	id: string;
	storage: IssueStorageBreakdown | undefined;
}) {
	const t = useT();
	if (!storage) {
		return (
			<div id={id} className={styles['details']}>
				{t('downloads.calculating')}
			</div>
		);
	}

	return (
		<dl id={id} className={styles['details']}>
			<div className={styles['details-row']}>
				<dt>{t('downloads.images')}</dt>
				<dd>
					{storage.images.count} · {formatMegabytes(storage.images.bytes)}
				</dd>
			</div>
			<div className={styles['details-row']}>
				<dt>{t('downloads.articles')}</dt>
				<dd>
					{storage.articles.count} ·{' '}
					{formatMegabytes(storage.articles.bytes)}
				</dd>
			</div>
			<div className={styles['details-row']}>
				<dt>{t('downloads.stylesExclusive')}</dt>
				<dd>
					{storage.exclusiveStyles.count} ·{' '}
					{formatMegabytes(storage.exclusiveStyles.bytes)}
				</dd>
			</div>
			<div className={styles['details-row']}>
				<dt>{t('downloads.stylesShared')}</dt>
				<dd>
					{storage.sharedStyles.count} ·{' '}
					{formatMegabytes(storage.sharedStyles.bytes)}
				</dd>
			</div>
			<div className={styles['details-row']}>
				<dt>{t('downloads.issueData')}</dt>
				<dd>{formatMegabytes(storage.issueRow.bytes)}</dd>
			</div>
			<div className={`${styles['details-row']} ${styles['details-total']}`}>
				<dt>{t('downloads.freed')}</dt>
				<dd>{formatMegabytes(storage.totalBytes)}</dd>
			</div>
			{storage.sharedStyles.count > 0 ? (
				<p className={styles['details-note']}>
					{t('downloads.sharedNote')}
				</p>
			) : null}
		</dl>
	);
}
